import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid";
import WebURL from "../../../../../urls";
import {
  Box,
  Checkbox,
  Dialog as MuiDialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import CustomDataGrid, { DataGridRef } from "@/components/data-grid/CustomDataGrid";
import { ATTEndpoints } from "@/services/att/endpoints";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import SnackbarUtil from "@/utils/SnackbarUtil";
import moment from "moment/moment";
import TimecardService from "@/services/att/services/TimecardService";
import ExportAction from "@/components/actions/Export";
import { DataGridContext } from "@/components/data-grid/DataGridContext";
import Util from "@/utils/Util";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import ReportShare from "./ReportShare";
import CompanyService from "@/services/auth/CompanyService";
import ColumnSettingsDialog from "@/components/column-settings/index";
import { ColumnSettings, getColumnSettings, getDefaultColumnSettings } from "@/services/Template/TemplateService";
import GenericService from "@/services/GenericService";

function isValidDate(date: any) {
  return (
    date instanceof Date && !isNaN(date.getTime()) && date.getFullYear() > 2000
  );
}

const punchRecordStyle = {
  "& .MuiPaper-root": { width: "60vw", top: "-5vh" },
  ".MuiDialog-paper": { maxWidth: "100vw" },
};

const Timecard: React.FC = () => {
  const reportShareRef = useRef<any>({});
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState<boolean>(false);
  const today = new Date();
  const firstDay = new Date();
  firstDay.setDate(1);
  const [exportFormat, setExportFormat] = useState<string>("");
  const [exportLoading, setExportLoading] = useState(false); // 下载 Loading

  const setFilter = (newValue: Record<any, any>) => {
    const { month } = newValue;
    if (!month) {
      return;
    }
    setExportFormat("");
    setFilterParams(newValue);
  };

  const [start_date, setStartDate] = useState<Date>(firstDay);
  const [end_date, setEndDate] = useState<Date>(new Date());
  const [selectValue, setSelectValue] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(0);
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy");
  const [userColumnConfig, setUserColumnConfig] = useState<any[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<boolean>(false)
  const [exportReport, setExportReport] = useState<string>('0')

  useEffect(() => {
    CompanyService.dateFormat({ method: "get" }).then((res) => {
      const date_format = res.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);
    });
    const fetchSettings = async () => {
      GenericService.list(ATTEndpoints.ATT_ATTENDANCE_SETTING_URI, {current: 0, pageSize: 100}).then((res) => {
        if (res.data && res.status === 200) {
          setPaymentStatus(res.data.subscription_status)
          setExportReport(res.data.export_report)
        }
      })
    };
    fetchSettings();
  }, []);

  const ExtraAction = (props: any) => {
    const { page } = useContext(DataGridContext);
    const { pageSize } = useContext(DataGridContext);
    const { searchKeyword } = useContext(DataGridContext);
    setPage(page);
    setPageSize(pageSize);
    setSearchKeyword(searchKeyword);

    return (
      <Fragment>
        <Grid item={true}>
          <Box>
            <Grid
              container={true}
              sx={{
                alignItems: "center",
                marginLeft: "5px",
              }}
            >
              <Box sx={{ width: "150px" }}>
                <DatePicker
                  disableFuture
                  views={["year", "month", "day"]}
                  value={start_date}
                  maxDate={end_date}
                  inputFormat={dateFormat}
                  onChange={(newValue: any) => {
                    if (!newValue) {
                      SnackbarUtil.error(
                        t("export.Start date cannot be null."),
                        {
                          anchorOrigin: {
                            vertical: "top",
                            horizontal: "center",
                          },
                        },
                      );
                      return;
                    }
                    setStartDate(newValue);
                    setFilterParams({
                      abnormal_records: selectValue,
                      date_range: [
                        Util.datetime2string(newValue, "yyyy-MM-dd"),
                        Util.datetime2string(end_date, "yyyy-MM-dd"),
                      ],
                    });
                    if (props.setFilterParams) {
                      props.setFilterParams({
                        ...props?.filterParams,
                        ...{
                          date_range: Util.datetime2string(
                            newValue,
                            "yyyy-MM-dd",
                          ),
                        },
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      size={"small"}
                      variant="outlined"
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                />
              </Box>
              -
              <Box sx={{ width: "150px" }}>
                <DatePicker
                  disableFuture
                  views={["year", "month", "day"]}
                  value={end_date}
                  minDate={start_date}
                  inputFormat={dateFormat}
                  onChange={(newValue: any) => {
                    if (!newValue) {
                      SnackbarUtil.error(t("export.End date cannot be null."), {
                        anchorOrigin: {
                          vertical: "top",
                          horizontal: "center",
                        },
                      });
                      return;
                    }
                    setEndDate(newValue);
                    setFilterParams({
                      abnormal_records: selectValue,
                      date_range: [
                        Util.datetime2string(start_date, "yyyy-MM-dd"),
                        Util.datetime2string(newValue, "yyyy-MM-dd"),
                      ],
                    });
                    if (props.setFilterParams) {
                      props.setFilterParams({
                        ...props?.filterParams,
                        ...{
                          date_range: Util.datetime2string(
                            newValue,
                            "yyyy-MM-dd",
                          ),
                        },
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      size={"small"}
                      variant="outlined"
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Box>
        </Grid>
        <Grid>
          <FormControlLabel
            sx={{ color: "#999999", fontSize: "14px" }}
            control={
              <Checkbox
                checked={selectValue}
                sx={{ marginLeft: "20px" }}
                onChange={(event) => {
                  const new_value = event.target.checked;
                  setSelectValue(new_value);
                  setFilterParams({
                    abnormal_records: new_value,
                    date_range: [
                      Util.datetime2string(start_date, "yyyy-MM-dd"),
                      Util.datetime2string(end_date, "yyyy-MM-dd"),
                    ],
                  });
                }}
              />
            }
            label={t("common.Display Only Abnormal Records")}
          />
        </Grid>
      </Fragment>
    );
  };
  useEffect(() => {
    if (exportFormat) {
      setExportLoading(true);

      let export_format = exportFormat;
      let with_tz = true;
      if (exportFormat === "xlsx with tz") {
        export_format = "xlsx";
        with_tz = true;
      }
      if (exportFormat === "csv with tz") {
        export_format = "csv";
        with_tz = true;
      }
      if (exportFormat === "xlsx" || exportFormat === "csv") {
        with_tz = false;
      }

      const export_meta = {
        fields: {
          employee_code: { alias: t("common.Person ID") },
          first_name: { alias: t("common.First Name") },
          last_name: { alias: t("common.Last Name") },
          att_date: { alias: t("common.Date") },
          timesheet_name: { alias: t("common.Timesheet") },
          clock_in: { alias: t("common.Clock In") },
          clock_out: { alias: t("common.Clock Out") },
          period_hrs: { alias: t("common.Clock Time(h)") },
          paid_break: { alias: t("common.Total Break Time(h)") },
          paid_work_deduct_break: { alias: t("common.Total Work Time(h)") },
          overtime_hrs: { alias: t("common.Total Overtime Time(h)") },
          paid_work: { alias: t("common.Total Time(h)") },
          pairing: { alias: t("common.Statistic Rule Mode") },
          abnormal_situation: { alias: t("common.Abnormal Situation") },
        },
        title: t("common.Timecard Report"),
        file_type: export_format,
        share: [],
        with_tz: with_tz,
      };

      if (
        !start_date ||
        !end_date ||
        !isValidDate(start_date) ||
        !isValidDate(end_date)
      ) {
        SnackbarUtil.error(
          "The start date and end date are required, Please select the correct start and end dates",
          { anchorOrigin: { vertical: "top", horizontal: "center" } },
        );
        setExportLoading(false);
        setExportFormat("");
        return;
      }

      try {
        const data = export_meta;
        let params = "";

        setFilterParams({
          abnormal_records: selectValue,
          date_range: [
            Util.datetime2string(start_date, "yyyy-MM-dd"),
            Util.datetime2string(end_date, "yyyy-MM-dd"),
          ],
          all: true,
        });

        params = `current=${page + 1}&pageSize=${pageSize}&date_range=${moment(start_date).format("yyyy-MM-DD")}&date_range=${moment(end_date).format("yyyy-MM-DD")}&keyword=${searchKeyword}&abnormal_records=${selectValue}&all=${true}`;

        TimecardService.download_data(params, data, "timecard")
          .then((response) => {
            if (response.status == 206) {
              SnackbarUtil.warning(t("common.No schedule data available. Please create schedules before download."), {
                  anchorOrigin: {vertical: 'top', horizontal: 'center'}
                });
              return
            }
            const blob = new Blob([response?.data], {
              type: response?.headers["content-type"],
              endings: "transparent",
            });
            if (export_format == 'xlsx') {
              const contentDisposition = response.headers["content-disposition"];
              let fileName = "Timecard Report.xlsx";
              if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match != null && match[1]) {
                  fileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
                }
              }
              const nav = window.navigator as any;
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(blob, fileName);
              } else {
                const url = URL.createObjectURL(blob);
                const aLink = document.createElement("a");
                aLink.href = url;
                aLink.download = fileName;
                aLink.click();

                setTimeout(() => {
                  URL.revokeObjectURL(url);
                }, 100);
              }
              SnackbarUtil.success(t("common.success"));
            } else {
              const url = URL.createObjectURL(blob);
              const aLink = document.createElement("a");
              aLink.style.display = "none";
              aLink.href = url;
              document.body.appendChild(aLink);
              aLink.click();
              URL.revokeObjectURL(url);
              document.body.removeChild(aLink);
              SnackbarUtil.success(t("common.success"));
            }
          })
          .catch((e) => {
            console.error("下载失败", e);
            SnackbarUtil.error(t("common.Download") + t("common.failure"), {
              anchorOrigin: {
                vertical: "top",
                horizontal: "center"
              }
            });
          })
          .finally(() => {
            setExportLoading(false);
            setExportFormat("");
          });

      } catch (e) {
        setExportLoading(false);
        setExportFormat("");
      }
    }
  }, [exportFormat, page, pageSize, searchKeyword, selectValue, start_date, end_date, t]);

  useEffect(() => {
    dataGridRef.current?.refresh();
  }, [i18n.language]);

  function debounce(fn: any, delay: any) {
    let timer: any;
    return function (...args: any) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  const debouncedSave = debounce((newValue: any) => {
    dataGridRef.current?.refresh();
  }, 1000);

  useEffect(() => {
    debouncedSave(selectValue);
  }, [selectValue, start_date, end_date]);

  const defaultColumns: GridColDef[] = [
    {
      field: "employee_name",
      headerName: t("hr.employee.Person Name"),
      width: 200,
      filterable: false,
    },
    {
      field: "employee_code",
      headerName: t("hr.employee.Person ID"),
      width: 130,
      filterable: false,
    },
    {
      field: "att_date",
      headerName: t("common.Date"),
      type: "date",
      minWidth: 130,
    },
    {
      field: "timesheet_name",
      headerName: t("common.Timesheet"),
      filterable: false,
    },
  ];

  const navigate = useNavigate();
  const dataGridRef = useRef<DataGridRef>();
  const [filterParams, setFilterParams] = useState<Record<any, any>>({
    date_range: [
      firstDay.toISOString().slice(0, 10),
      today.toISOString().slice(0, 10),
    ],
  });

  const [gridColumn, setColumns] = useState<GridColDef[]>([]);

  const PairingField = (params: GridRenderCellParams) => {
    let pairing = "";
    switch (params.value) {
      case "First and Last":
        pairing = t("att.timeSheet.First and Last");
        break;
      case "Even and Odd":
        pairing = t("att.timeSheet.Even and Odd");
        break;
      default:
        break;
    }
    return <Grid>{pairing}</Grid>;
  };
  const AbnormalField = (params: GridRenderCellParams) => {
    let abnormal = "";
    switch (params.value) {
      case "Late":
        abnormal = t("att.timecard.Late");
        break;
      case "Early Departure":
        abnormal = t("att.timecard.Early Departure");
        break;
      case "Late,Early Departure":
        abnormal = t("att.timecard.Late And Early");
        break;
      default:
        break;
    }
    return <Grid>{abnormal}</Grid>;
  };

  const ExtendColumns = [
    {
      field: "clock_in",
      headerName: t("common.Clock In"),
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: any) => (
        <Tooltip title={`${params.row[`clock_in_origin`]}`} arrow>
          <span style={{ cursor: "pointer" }}>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "clock_out",
      headerName: t("common.Clock Out"),
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: any) => (
        <Tooltip title={`${params.row[`clock_out_origin`]}`} arrow>
          <span style={{ cursor: "pointer" }}>{params.value}</span>
        </Tooltip>
      ),
    },
    { field: "period_hrs", headerName: t("common.Clock Time(h)"), filterable: false, width: 120 },
    { field: "paid_work_deduct_break", headerName: t("common.Total Work Time(h)"), filterable: false, width: 120 },
    { field: "overtime_hrs", headerName: t("common.Total Overtime Time(h)"), filterable: false, width: 120 },
    { field: "paid_work", headerName: t("common.Total Time(h)"), filterable: false, width: 120 },
    { field: "paid_break", headerName: t("common.Total Break Time(h)"), filterable: false, width: 100 },
    { field: "pairing", headerName: t("common.Statistic Rule Mode"), renderCell: PairingField, filterable: false, width: 130 },
    { field: "abnormal_situation", headerName: t("common.Abnormal Situation"), filterable: false, renderCell: AbnormalField, width: 130 },
  ]

  const buildColumnsFromConfig = useCallback((config: any[], baseColumns: GridColDef[], extendColumns: GridColDef[]) => {
    if (!config || config.length === 0) {
      return [...baseColumns, ...extendColumns];
    }
    const sortedConfig = [...config].sort((a, b) => (a.sort || 0) - (b.sort || 0));
    const fullBaseColumns = [...baseColumns, ...extendColumns];
    const finalColumns: GridColDef[] = [];
    const dynamicFields = ['clock_in', 'clock_out', 'period_hrs'];

    sortedConfig.forEach((item) => {
      if (!item.isCheck) return;
      const exactMatch = fullBaseColumns.find(col => col.field === item.key);
      if (exactMatch) {
        finalColumns.push(exactMatch);
        return;
      }
      if (dynamicFields.includes(item.key)) {
        const dynamicMatches = fullBaseColumns.filter(col =>
          col.field.startsWith(`${item.key}`) && col.field !== item.key && /^\d+$/.test(col.field.replace(item.key, ''))
        );
        if (dynamicMatches.length > 0) {
          const sortedMatches = dynamicMatches.sort((a, b) => {
            const getIndex = (field: string) => {
              const match = field.match(/\d+$/);
              return match ? parseInt(match[0]) : 0;
            };
            return getIndex(a.field) - getIndex(b.field);
          });
          finalColumns.push(...sortedMatches);
        }
      }
    });
    return finalColumns;
  }, []);

  const handleColumnsChange = useCallback((newColumns: any[]) => {
    setUserColumnConfig(newColumns);
    const newGridColumns = buildColumnsFromConfig(newColumns, defaultColumns, ExtendColumns);
    setColumns(newGridColumns);
    setTimeout(() => { dataGridRef.current?.refresh(); }, 100);
  }, [defaultColumns, ExtendColumns, buildColumnsFromConfig]);

  const processResponse = useCallback((data: any) => {
    const calcColumns: GridColDef[] = [];
    let maxColumnsCount = 0;
    const result: any = [];
    if (data.data.length === 0) {
      calcColumns.push(...defaultColumns, ...ExtendColumns);
    } else {
      calcColumns.push(...defaultColumns);
      const max_seq = data?.max_seq || 0
      data.data.forEach((element: any) => {
        const len = element.paired.length;
        maxColumnsCount = Math.max(maxColumnsCount, len);
        maxColumnsCount = Math.min(maxColumnsCount, max_seq);
        let paired_objs = {};
        element.paired.forEach((paired_obj: any) => {
          paired_objs = {...paired_objs, ...paired_obj};
        });
        result.push({...element, ...paired_objs});
      });

      if (maxColumnsCount > 0) {
        for (let i = 0; i < maxColumnsCount; i++) {
          calcColumns.push({
            field: `clock_in${i}`,
            headerName: maxColumnsCount > 1 ? t("common.Clock In") + " " + `${i + 1}` : t("common.Clock In"),
            filterable: false,
            width: 110,
            renderCell: (params: any) => (
              <Tooltip
                title={`${params.row[`clock_in_origin${i}`]}`}
                arrow>
                <span style={{cursor: "pointer"}}>{params.value}</span>
              </Tooltip>
            ),
          });
          calcColumns.push({
            field: `clock_out${i}`,
            headerName: maxColumnsCount > 1 ? t("common.Clock Out") + " " + `${i + 1}` : t("common.Clock Out"),
            filterable: false,
            width: 110,
            renderCell: (params: any) => (
              <Tooltip
                title={`${params.row[`clock_out_origin${i}`]}`}
                arrow>
                <span style={{cursor: "pointer"}}>{params.value}</span>
              </Tooltip>
            ),
          });
          calcColumns.push({
            field: `period_hrs${i}`,
            headerName: maxColumnsCount > 1 ? t("common.Clock Time Value(h)", {value: `${i + 1}`}) : t("common.Clock Time(h)"),
            filterable: false,
            width: 120,
          });
        }
        calcColumns.push(
          { field: "paid_break", width: 100, headerName: t("common.Total Break Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_break || "", filterable: false },
          { field: "paid_work_deduct_break", width: 100, headerName: t("common.Total Work Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_work_deduct_break || "", filterable: false },
          { field: "overtime_hrs", width: 110, headerName: t("common.Total Overtime Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.overtime_hrs || "", filterable: false },
          { field: "paid_work", width: 120, headerName: t("common.Total Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_work || "", filterable: false },
          { field: "pairing", headerName: t("common.Statistic Rule Mode"), minWidth: 160, renderCell: PairingField, filterable: false },
          { field: "abnormal_situation", headerName: t("common.Abnormal Situation"), renderCell: AbnormalField, minWidth: 160, filterable: false }
        );
      } else if (max_seq > 0 && maxColumnsCount == 0) {
          calcColumns.push({
            field: `clock_in0`,
            headerName: maxColumnsCount > 1 ? t("common.Clock In") + " " + '1' : t("common.Clock In"),
            filterable: false,
            width: 110,
            renderCell: (params: any) => (
              <Tooltip
                title={`${params.row[`clock_in_origin0`]}`}
                arrow>
                <span style={{cursor: "pointer"}}>{params.value}</span>
              </Tooltip>
            ),
          });
          calcColumns.push({
            field: `clock_out0`,
            headerName: maxColumnsCount > 1 ? t("common.Clock Out") + " " + '1' : t("common.Clock Out"),
            filterable: false,
            width: 110,
            renderCell: (params: any) => (
              <Tooltip
                title={`${params.row[`clock_out_origin0`]}`}
                arrow>
                <span style={{cursor: "pointer"}}>{params.value}</span>
              </Tooltip>
            ),
          });
          calcColumns.push({
            field: `period_hrs0`,
            headerName: maxColumnsCount > 1 ? t("common.Clock Time Value(h)", {value: '1'}) : t("common.Clock Time(h)"),
            filterable: false,
            width: 120,
          });
          calcColumns.push(
          { field: "paid_break", width: 100, headerName: t("common.Total Break Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_break || "", filterable: false },
          { field: "paid_work_deduct_break", width: 100, headerName: t("common.Total Work Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_work_deduct_break || "", filterable: false },
          { field: "overtime_hrs", width: 110, headerName: t("common.Total Overtime Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.overtime_hrs || "", filterable: false },
          { field: "paid_work", width: 120, headerName: t("common.Total Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_work || "", filterable: false },
          { field: "pairing", headerName: t("common.Statistic Rule Mode"), minWidth: 160, renderCell: PairingField, filterable: false },
          { field: "abnormal_situation", headerName: t("common.Abnormal Situation"), renderCell: AbnormalField, minWidth: 160, filterable: false }
        );
        }else if (max_seq == 0 && maxColumnsCount == 0) {
          calcColumns.push({
            field: `clock_in0`,
            headerName: t("common.Clock In"),
            filterable: false,
            width: 110,
            renderCell: (params: any) => (
              <Tooltip
                title={`${params.row[`clock_in_origin0`]}`}
                arrow>
                <span style={{cursor: "pointer"}}>{params.value}</span>
              </Tooltip>
            ),
          });
          calcColumns.push({
            field: `clock_out0`,
            headerName: t("common.Clock Out"),
            filterable: false,
            width: 110,
            renderCell: (params: any) => (
              <Tooltip
                title={`${params.row[`clock_out_origin0`]}`}
                arrow>
                <span style={{cursor: "pointer"}}>{params.value}</span>
              </Tooltip>
            ),
          });
          calcColumns.push({
            field: `period_hrs0`,
            headerName: t("common.Clock Time(h)"),
            filterable: false,
            width: 120,
          });
          calcColumns.push(
          { field: "paid_break", width: 100, headerName: t("common.Total Break Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_break || "", filterable: false },
          { field: "paid_work_deduct_break", width: 100, headerName: t("common.Total Work Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_work_deduct_break || "", filterable: false },
          { field: "overtime_hrs", width: 110, headerName: t("common.Total Overtime Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.overtime_hrs || "", filterable: false },
          { field: "paid_work", width: 120, headerName: t("common.Total Time(h)"), valueGetter: (params: GridValueGetterParams) => params.row.payload?.paid_work || "", filterable: false },
          { field: "pairing", headerName: t("common.Statistic Rule Mode"), minWidth: 160, renderCell: PairingField, filterable: false },
          { field: "abnormal_situation", headerName: t("common.Abnormal Situation"), renderCell: AbnormalField, minWidth: 160, filterable: false }
        );
        } else {
        calcColumns.push(...ExtendColumns);
      }
    }

    if (userColumnConfig.length > 0) {
      const dynamicFields = ['clock_in', 'clock_out', 'period_hrs'];
      const finalColumns: GridColDef[] = [];
      const sortedConfig = [...userColumnConfig].sort((a, b) => (a.sort || 0) - (b.sort || 0));
      sortedConfig.forEach((item) => {
        if (!item.isCheck) return;
        const exactMatch = calcColumns.find(col => col.field === item.key);
        if (exactMatch) {
          finalColumns.push(exactMatch);
          return;
        }
        if (dynamicFields.includes(item.key)) {
          const dynamicMatches = calcColumns.filter(col =>
            col.field.startsWith(`${item.key}`) && col.field !== item.key
          );
          if (dynamicMatches.length > 0) {
            const sortedMatches = dynamicMatches.sort((a, b) => {
              const getIndex = (field: string) => {
                const match = field.match(/\d+$/);
                return match ? parseInt(match[0]) : 0;
              };
              return getIndex(a.field) - getIndex(b.field);
            });
            finalColumns.push(...sortedMatches);
          }
        }
      });
      setColumns(finalColumns);
    } else {
      setColumns(calcColumns);
    }

    return { data: result, total: data.total };
  }, [defaultColumns, ExtendColumns, userColumnConfig, t]);

  const [columnReady, setColumnReady] = useState(false);

  useEffect(() => {
    const loadColumnSettings = async () => {
      try {
        let columnData: ColumnSettings[] = [];
        const res = await getColumnSettings('timecard');
        if (res.data?.data.length === 0) {
          const defaultRes = await getDefaultColumnSettings('timecard');
          columnData = defaultRes as ColumnSettings[];
        } else {
          columnData = res.data?.data as ColumnSettings[];
        }

        const activeColumn = columnData.find((item) => item.is_active) || columnData[0];
        if (activeColumn) {
          const templateFieldArray = typeof activeColumn.template_field === 'string'
            ? JSON.parse(activeColumn.template_field)
            : activeColumn.template_field;
          const columnsConfig = templateFieldArray.map((item: any) => ({
            label: item.label,
            key: item.key,
            isCheck: item.isCheck,
            required: item.required,
            sort: item.sort,
          }));
          setUserColumnConfig(columnsConfig);
          const allColumns = [...defaultColumns, ...ExtendColumns];
          const matchedColumns = (() => {
            const dynamicFields = ['clock_in', 'clock_out', 'period_hrs'];
            const result: GridColDef[] = [];
            const sortedConfig = [...columnsConfig].sort((a, b) => (a.sort || 0) - (b.sort || 0));
            sortedConfig.forEach((item: any) => {
              if (!item.isCheck) return;
              const exactMatch = allColumns.find(col => col.field === item.key);
              if (exactMatch) {
                result.push(exactMatch);
                return;
              }
              if (dynamicFields.includes(item.key)) {
                const dynamicMatches = allColumns.filter(col =>
                  col.field.startsWith(`${item.key}`) && col.field !== item.key
                );
                if (dynamicMatches.length > 0) {
                  const sortedMatches = dynamicMatches.sort((a, b) => {
                    const getIndex = (field: string) => {
                      const match = field.match(/\d+$/);
                      return match ? parseInt(match[0]) : 0;
                    };
                    return getIndex(a.field) - getIndex(b.field);
                  });
                  result.push(...sortedMatches);
                }
              }
            });
            return result.length > 0 ? result : allColumns;
          })();
          setColumns(matchedColumns);
        } else {
          setUserColumnConfig([]);
          setColumns([...defaultColumns, ...ExtendColumns]);
        }
      } catch (error) {
        console.error('Failed to load column settings:', error);
        setUserColumnConfig([]);
        setColumns([...defaultColumns, ...ExtendColumns]);
      } finally {
        setColumnReady(true);
      }
    };
    loadColumnSettings();
  }, []);

  const toolbar = useMemo(() => ({
    title: t("common.Timecard Management"),
    searchTips: t("common.Search by Person ID, Person Name, Timesheet Name"),
    search: true,
    breadcrumbs: [
      {
        breadcrumbName: t("common.Timecard Management"),
        path: WebURL.ATT_TIMECARD_TIMECARD,
      },
    ],
    children: (
      <ExtraAction
        filterParams={filterParams}
        setFilterParams={setFilter}
        setExportFormat={setExportFormat}
      />
    ),
    actions: [
      {
        key: "about",
        iconFont: "icon-ic_about",
        helpTxt: t("common.Usage Tips"),
        callback: async () => setInfo(true),
      },
      {
        key: "export",
        iconFont: "icon-ic_export",
        helpTxt: t("export.Export"),
        callback: async () => reportShareRef?.current?.showReport(),
      },
    ],
    extraComponentOnRight: (
      <Grid sx={{ marginRight: "-15px" }}>
        <ColumnSettingsDialog
          report_type="timecard"
          onColumnsChange={handleColumnsChange}
        />
        <ExportAction
          setExportFormat={(fileType: string) => setExportFormat(fileType)}
          report_type="timecard"
          loading={exportLoading}
        />
      </Grid>
    ),
  }), [t, filterParams, handleColumnsChange, exportLoading]);

  const InfoDialog = () => {
    return (
      <MuiDialog open={info} sx={punchRecordStyle}>
        <DialogTitle>
          {t("common.Usage Tips")}
          <IconButton
            aria-label="close"
            onClick={() => setInfo(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ lineHeight: "25px", color: "gray" }}>
          <Box sx={{ p: 1 }}> 1. {t("dms.TimecardDataTip")}</Box>
          <Box sx={{ p: 1 }}> 2. {t("dms.TimecardDateTip")}</Box>
        </DialogContent>
      </MuiDialog>
    );
  };
  return (
    <>
      <Box sx={{ "& .MuiDataGrid-row-height-no-limit": { maxHeight: "180px!important" } }}>
        <CustomBreadcrumbs record={[{ label: t("common.Timecard Management") }]} />
        {columnReady && (
          <CustomDataGrid
            columns={gridColumn}
            ref={dataGridRef}
            uri={ATTEndpoints.ATT_TIMECARD_URI}
            requestParams={filterParams}
            toolbar={toolbar}
            processResponse={processResponse}
            localeText={{
              filterPanelColumns: t("common.Columns"),
              filterPanelOperators: t("common.Operators"),
            }}
          />
        )}
      </Box>
      <InfoDialog />
      <ReportShare date_format={dateFormat} ref={reportShareRef} payment_status={paymentStatus}
                   export_report={exportReport}/>
    </>
  );
};

export default Timecard;
